import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Box, Button, Collapse, Stack, Typography } from '@mui/material'
import WifiOffRoundedIcon from '@mui/icons-material/WifiOffRounded'
import WifiTetheringErrorRoundedIcon from '@mui/icons-material/WifiTetheringErrorRounded'
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded'

const ConnectionStatusBanner = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [slowUntil, setSlowUntil] = useState(null)

  useEffect(() => {
    const handleOnline = () => setIsOnline(true)
    const handleOffline = () => setIsOnline(false)

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  useEffect(() => {
    const handleSlowConnection = () => {
      setSlowUntil(Date.now() + 30000)
    }

    window.addEventListener('ambunet:slow-connection', handleSlowConnection)

    const interval = setInterval(() => {
      setSlowUntil((prev) => {
        if (!prev) return null
        return Date.now() > prev ? null : prev
      })
    }, 1000)

    return () => {
      window.removeEventListener(
        'ambunet:slow-connection',
        handleSlowConnection,
      )
      clearInterval(interval)
    }
  }, [])

  const status = useMemo(() => {
    if (!isOnline) {
      return {
        open: true,
        severity: 'error',
        icon: <WifiOffRoundedIcon />,
        title: 'Connection lost',
        message:
          'AmbuNet is currently unable to connect. Changes may not save until your connection returns.',
      }
    }

    if (slowUntil) {
      return {
        open: true,
        severity: 'warning',
        icon: <WifiTetheringErrorRoundedIcon />,
        title: 'Connection is unstable',
        message:
          'Some requests are taking longer than expected. Please wait before refreshing or trying again.',
      }
    }

    return { open: false }
  }, [isOnline, slowUntil])

  if (!status.open) {
    return null
  }

  return (
    <Box sx={{ px: 2, pt: 1 }}>
      <Alert
        severity={status.severity}
        icon={status.icon}
        action={
          <Button
            size="small"
            startIcon={<RefreshRoundedIcon />}
            onClick={() => window.location.reload()}
            sx={{ fontWeight: 800 }}
          >
            Reload
          </Button>
        }
        sx={{
          borderRadius: 2,
          alignItems: 'center',
        }}
      >
        <Stack>
          <Typography fontWeight={900} variant="body2">
            {status.title}
          </Typography>
          <Typography variant="body2">{status.message}</Typography>
        </Stack>
      </Alert>
    </Box>
  )
}

export default ConnectionStatusBanner
